import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  FaCircleNotch,
  FaAngleLeft,
  FaAngleRight,
  FaXmark,
  FaNoteSticky,
  FaListCheck,
  FaChevronDown,
  FaPlus,
  FaLock,
} from "react-icons/fa6";
import { getSyncUserActivities } from "../../api/mainApi"; // Імпортуємо метод для GET-запиту

// Словник кольорів для індикаторів
const DOT_COLORS = {
  amber: "bg-amber-400",
  emerald: "bg-emerald-400",
  cyan: "bg-cyan-400",
  purple: "bg-purple-400",
  rose: "bg-rose-400",
};

const Calendar = ({
  tg,
  allMoods,
  regDate: parentRegDate,
  loading,
  setLoading,
}) => {
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [days, setDays] = useState([]);
  const [firstDayOfWeek, setFirstDayOfWeek] = useState(null);
  const [regDate, setRegDate] = useState(parentRegDate);

  // Стан для обраного дня (для відображення попапу з деталями)
  const [selectedDay, setSelectedDay] = useState(null);

  // Стан для відкриття меню вибору дати (Рік/Місяць)
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  // Тимчасовий рік для пікера (щоб перемикати місяці для обраного року)
  const [pickerYear, setPickerYear] = useState(new Date().getFullYear());

  // Стан для обробки свайпів
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const MIN_SWIPE_DISTANCE = 50;

  useEffect(() => {
    const locale =
      tg.initDataUnsafe?.user?.language_code || navigator.language || "en-US";
    try {
      const options = new Intl.Locale(locale);
      const weekStartsOnMonday = options.weekInfo?.firstDay === 1;
      setFirstDayOfWeek(weekStartsOnMonday ? 1 : 0);
    } catch (error) {
      setFirstDayOfWeek(0);
    }
  }, [tg]);

  // Фонове завантаження шаблонів активностей (як у MoodMenu)
  useEffect(() => {
    const fetchActivities = async () => {
      try {
        if (localStorage.getItem("moodActivitiesChanged") === "true") return;

        const data = await getSyncUserActivities();
        if (data && Array.isArray(data.activities) && data.activities.length > 0) {
          localStorage.setItem("moodActivities", JSON.stringify(data.activities));
        }
      } catch (error) {
        console.error("Error fetching user activities:", error);
      }
    };

    if (tg?.initData) {
      fetchActivities();
    }
  }, [tg?.initData]);

  // Синхронізація pickerYear при відкритті меню або зміні дати
  useEffect(() => {
    if (isDatePickerOpen) {
      setPickerYear(currentDate.getFullYear());
    }
  }, [isDatePickerOpen, currentDate]);

  const daysInWeek = [
    t("sun"),
    t("mon"),
    t("tue"),
    t("wed"),
    t("thu"),
    t("fri"),
    t("sat"),
  ];
  const orderedDays =
    firstDayOfWeek === 1 ? [...daysInWeek.slice(1), t("sun")] : daysInWeek;

  const getMoodImage = (mood, additionalMood = null) => {
    if (mood) {
      const suffix = additionalMood ? `.${additionalMood}` : "";
      return `/${mood}${suffix}.webp`;
    }
    return "/0.webp";
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const processMoodData = (moodsData, date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const totalDays = new Date(year, month + 1, 0).getDate();
    let firstDayIndex = new Date(year, month, 1).getDay();

    if (firstDayOfWeek === 1) {
      firstDayIndex = firstDayIndex === 0 ? 6 : firstDayIndex - 1;
    }

    const daysArray = Array.from({ length: totalDays }, (_, i) => ({
      day: i + 1,
      moods: [],
    }));

    moodsData.forEach((item) => {
      const moodDate = new Date(item.date * 1000);
      if (moodDate.getFullYear() === year && moodDate.getMonth() === month) {
        const day = moodDate.getDate();
        const index = day - 1;
        daysArray[index].moods.push(item);
      }
    });

    return Array(firstDayIndex).fill(null).concat(daysArray);
  };

  const fetchMonthData = (date) => {
    setLoading(true);
    try {
      const filteredMoods = allMoods.filter((mood) => {
        const moodDate = new Date(mood.date * 1000);
        return (
          moodDate.getFullYear() === date.getFullYear() &&
          moodDate.getMonth() === date.getMonth()
        );
      });
      const monthData = processMoodData(filteredMoods, date);
      setDays(monthData);
    } catch (error) {
      alert("Помилка завантаження даних для календаря:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (firstDayOfWeek !== null && allMoods) {
      fetchMonthData(currentDate);
      setSelectedDay(null);
    }
  }, [currentDate, firstDayOfWeek, allMoods]);

  // --- ЛОГІКА НАВІГАЦІЇ ---

  const canGoPrev =
    parentRegDate &&
    (currentDate.getFullYear() > parentRegDate.getFullYear() ||
      (currentDate.getFullYear() === parentRegDate.getFullYear() &&
        currentDate.getMonth() > parentRegDate.getMonth()));

  const canGoNext =
    currentDate.getFullYear() < new Date().getFullYear() ||
    (currentDate.getFullYear() === new Date().getFullYear() &&
      currentDate.getMonth() < new Date().getMonth());

  const handlePrevMonth = () => {
    if (canGoPrev) {
      setCurrentDate(
        new Date(currentDate.setMonth(currentDate.getMonth() - 1))
      );
    }
  };

  const handleNextMonth = () => {
    if (canGoNext) {
      setCurrentDate(
        new Date(currentDate.setMonth(currentDate.getMonth() + 1))
      );
    }
  };

  // --- ЛОГІКА СВАЙПІВ ---
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > MIN_SWIPE_DISTANCE;
    const isRightSwipe = distance < -MIN_SWIPE_DISTANCE;

    if (isLeftSwipe) handleNextMonth();
    if (isRightSwipe) handlePrevMonth();
  };

  // --- ЛОГІКА DATE PICKER ---
  
  // Генерація списку років (від regDate до поточного)
  const getAvailableYears = () => {
    const startYear = parentRegDate ? parentRegDate.getFullYear() : 2024;
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = startYear; y <= currentYear; y++) {
      years.push(y);
    }
    return years; 
  };

  // Перевірка чи доступний місяць для вибору
  const isMonthDisabled = (year, monthIndex) => {
    const now = new Date();
    // Не можна в майбутнє
    if (year === now.getFullYear() && monthIndex > now.getMonth()) return true;
    if (year > now.getFullYear()) return true;

    // Не можна раніше дати реєстрації
    if (parentRegDate) {
      if (year === parentRegDate.getFullYear() && monthIndex < parentRegDate.getMonth()) return true;
      if (year < parentRegDate.getFullYear()) return true;
    }
    return false;
  };

  const handleMonthSelect = (monthIndex) => {
    if (isMonthDisabled(pickerYear, monthIndex)) return;
    
    const newDate = new Date(currentDate);
    newDate.setFullYear(pickerYear);
    newDate.setMonth(monthIndex);
    newDate.setDate(1); // Скидаємо на 1 число щоб уникнути багів з 31 числом
    
    setCurrentDate(newDate);
    setIsDatePickerOpen(false);
  };

  // Назви місяців для пікера
  const monthNames = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(2000, i, 1);
    return d.toLocaleString("default", { month: "long" }); 
  });

  if (regDate === null) {
    return (
      <div className="text-center h-96 flex items-center justify-center">
        <FaCircleNotch className="w-24 h-24 opacity-50 text-slate-950 animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Header Calendar */}
      <div className="flex justify-between items-center gap-x-4 mb-4 relative z-30">
        <div 
          onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
          className="flex items-center gap-2 cursor-pointer hover:bg-slate-800 px-2 py-1 rounded-lg transition-colors select-none"
        >
          <h2 className="uppercase font-bold text-xl">
            {currentDate.toLocaleString("default", { month: "long" })}{" "}
            {currentDate.getFullYear()}
          </h2>
          <FaChevronDown className={`text-sm transition-transform duration-300 ${isDatePickerOpen ? "rotate-180" : ""}`} />
        </div>

        <div className="flex items-center gap-x-4">
          <button
            onClick={handlePrevMonth}
            className={`p-2 rounded-full transition-colors ${
              canGoPrev
                ? "bg-slate-800 hover:bg-slate-700"
                : "bg-slate-500 opacity-50 cursor-not-allowed"
            }`}
            disabled={!canGoPrev}
          >
            <FaAngleLeft />
          </button>
          <button
            onClick={handleNextMonth}
            className={`p-2 rounded-full transition-colors ${
              canGoNext
                ? "bg-slate-800 hover:bg-slate-700"
                : "bg-slate-500 opacity-50 cursor-not-allowed"
            }`}
            disabled={!canGoNext}
          >
            <FaAngleRight />
          </button>
        </div>
      </div>

      {/* OVERLAY DATE PICKER */}
      <div 
        className={`absolute inset-x-0 top-12 bottom-0 bg-slate-900 z-20 transition-all duration-300 ease-in-out flex flex-col overflow-hidden ${
          isDatePickerOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
        }`}
      >
        <div className="flex flex-1 h-full">
          {/* Колонка років */}
          <div className="w-1/3 border-r border-slate-700 overflow-y-auto hidden-scrollbar py-2">
             <div className="flex flex-col gap-1 px-2">
                {getAvailableYears().map((year) => (
                  <button
                    key={year}
                    onClick={() => setPickerYear(year)}
                    className={`py-3 text-sm font-bold rounded-lg transition-colors ${
                      pickerYear === year 
                        ? "bg-emerald-600 text-white shadow-md" 
                        : "text-slate-400 hover:bg-slate-800"
                    }`}
                  >
                    {year}
                  </button>
                ))}
             </div>
          </div>

          {/* Колонка місяців */}
          <div className="w-2/3 overflow-y-auto hidden-scrollbar py-2">
            <div className="grid grid-cols-2 gap-2 px-2">
              {monthNames.map((mName, index) => {
                const disabled = isMonthDisabled(pickerYear, index);
                const isSelected = 
                  pickerYear === currentDate.getFullYear() && 
                  index === currentDate.getMonth();

                return (
                  <button
                    key={index}
                    disabled={disabled}
                    onClick={() => handleMonthSelect(index)}
                    className={`py-3 text-sm font-semibold rounded-lg capitalize transition-all ${
                       isSelected 
                       ? "bg-slate-700 text-emerald-400 border border-emerald-500/50"
                       : disabled 
                         ? "text-slate-700 cursor-not-allowed" 
                         : "text-slate-200 hover:bg-slate-800 active:scale-95 bg-slate-800/50"
                    }`}
                  >
                    {mName}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Days Names */}
      <div className="grid grid-cols-7 text-center mb-2 font-bold select-none">
        {orderedDays.map((day) => (
          <div key={day}>{day}</div>
        ))}
      </div>

      {/* Days Grid + Swipe Area */}
      <div
        className="grid grid-cols-7 gap-2 touch-pan-y"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {loading
          ? Array.from({ length: 35 }).map((_, i) => (
              <div
                key={i}
                className="p-1 flex flex-col items-center justify-center rounded-lg bg-slate-800"
              >
                <div className="w-5 h-5 bg-white rounded-md animate-pulse mb-px"></div>
                <img
                  src="0.1.webp"
                  className="w-full aspect-square block "
                  alt=""
                />
              </div>
            ))
          : days.map((item, index) => {
              if (!item) return <div key={index} className="p-1"></div>;

              const today = new Date();
              const isToday =
                today.getFullYear() === currentDate.getFullYear() &&
                today.getMonth() === currentDate.getMonth() &&
                item.day === today.getDate();

              const hasMoods = item.moods && item.moods.length > 0;
              const latestMood = hasMoods
                ? item.moods[item.moods.length - 1]
                : null;

              const hasNotes =
                hasMoods &&
                item.moods.some((m) => m.note && m.note.trim() !== "");

              const hasFollowUp =
                !hasNotes &&
                hasMoods &&
                item.moods.some((m) => m.follow_up && m.follow_up.question);

              const imageSrc = latestMood
                ? getMoodImage(latestMood.mood, latestMood.additional_mood)
                : isToday
                ? "0.webp"
                : "0.1.webp";

              const cellBg = isToday
                ? "bg-gradient-to-b from-emerald-500 to-cyan-500 shadow-lg scale-105"
                : "bg-slate-800";

              return (
                <div
                  key={index}
                  onClick={() => hasMoods && setSelectedDay(item)}
                  className={`p-1 relative flex flex-col items-center justify-center rounded-lg transition-transform active:scale-95 select-none ${cellBg} ${
                    hasMoods ? "cursor-pointer hover:bg-slate-700" : ""
                  }`}
                >
                  <span className="text-sm font-semibold">{item.day}</span>
                  <div className="relative w-full">
                    <img
                      src={imageSrc}
                      className="mx-auto aspect-square block object-contain"
                      alt={latestMood ? `Mood` : "No mood"}
                    />
                    {item.moods.length > 1 && (
                      <div className="absolute bottom-0 right-0 bg-white text-slate-900 w-4 h-4 flex items-center justify-center rounded-full font-bold shadow-sm">
                        <FaPlus className="text-[10px]" />
                      </div>
                    )}
                  </div>
                  {hasNotes && (
                    <div className="absolute bottom-1 left-1 bg-white w-4 h-4 flex items-center justify-center rounded-full text-yellow-300 drop-shadow-md">
                      <FaNoteSticky className="text-[10px]" />
                    </div>
                  )}
                  {hasFollowUp && (
                    <div className="absolute bottom-1 left-1 bg-white w-4 h-4 flex items-center justify-center rounded-full text-purple-400 drop-shadow-md">
                      <FaListCheck className="text-[10px]" />
                    </div>
                  )}
                </div>
              );
            })}
      </div>

      {/* POPUP (Bottom Sheet) Details */}
      {selectedDay && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-40 backdrop-blur-sm transition-opacity"
            onClick={() => setSelectedDay(null)}
          />

          <div className="fixed bottom-0 left-0 w-full bg-slate-900 rounded-t-3xl z-50 shadow-[0_-5px_30px_rgba(0,0,0,0.5)] border-t border-slate-700 animate-slide-up max-h-[80vh] overflow-y-auto hidden-scrollbar lg:custom-scrollbar">
            <div className="p-5">
              <div className="flex justify-between items-center mb-6">
                <div className="flex flex-col text-left">
                  <h3 className="text-2xl font-bold text-white">
                    {selectedDay.day}
                  </h3>
                  <span className="text-slate-400 text-sm uppercase font-semibold tracking-wider">
                    {currentDate.toLocaleString("default", { month: "long" })}
                  </span>
                </div>
                <button
                  onClick={() => setSelectedDay(null)}
                  className="p-2 bg-slate-800 rounded-full hover:bg-slate-700 hover:rotate-90 transition-all text-white/80"
                >
                  <FaXmark className="w-6 h-6" />
                </button>
              </div>

              <div className="flex flex-col gap-4">
                {selectedDay.moods
                  .slice()
                  .reverse()
                  .map((moodItem, i) => {
                    const hasItemActivities = moodItem.activities && moodItem.activities.length > 0;
                    const hasItemNote = moodItem.note && moodItem.note.trim() !== "";
                    const hasItemFollowUp = moodItem.follow_up && moodItem.follow_up.question;

                    return (
                      <div
                        key={i}
                        className="flex flex-col p-4 bg-slate-800 rounded-2xl border border-slate-700/50"
                      >
                        <div className="flex items-center gap-3 border-b border-white/5 pb-3 mb-3">
                          <div className="bg-slate-700/50 p-1.5 rounded-full">
                            <img
                              src={getMoodImage(
                                moodItem.mood,
                                moodItem.additional_mood
                              )}
                              className="w-10 h-10 object-contain"
                              alt="mood"
                            />
                          </div>
                          <div className="flex flex-col text-left">
                            <span className="text-xl font-bold text-white leading-none">
                              {formatTime(moodItem.date)}
                            </span>
                            <span className="text-xs text-slate-400 mt-1">
                              {t("loggedAt") || "Запис створено"}
                            </span>
                          </div>
                        </div>

                        <div className="text-left flex flex-col gap-3">
                          {/* Відображення активностей */}
                          {hasItemActivities && (
                            <div className="flex flex-wrap gap-2">
                              {moodItem.activities.map((act, idx) => {
                                // Визначаємо колір за збереженим значенням або за замовчуванням
                                const dotColor = DOT_COLORS[act.color] || DOT_COLORS.emerald;
                                
                                return (
                                  <div
                                    key={idx}
                                    className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-900/50 border border-white/5 shadow-sm rounded-lg"
                                  >
                                    <div className={`w-1.5 h-1.5 rounded-full ${dotColor} opacity-80`} />
                                    <span className="text-xs text-slate-200 font-medium">
                                      {act.text}
                                    </span>
                                    {act.isPrivate && (
                                      <FaLock className="w-2.5 h-2.5 text-slate-500 ml-0.5" />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {hasItemNote && (
                            <div className="bg-slate-900/50 p-3 rounded-xl border-l-2 border-yellow-500">
                              <div className="flex items-center gap-2 mb-1">
                                <FaNoteSticky className="text-yellow-500 text-xs" />
                                <span className="text-xs text-slate-400 uppercase font-bold">
                                  {t("note")}
                                </span>
                              </div>
                              <p className="text-slate-200 text-sm whitespace-pre-wrap leading-relaxed">
                                {moodItem.note}
                              </p>
                            </div>
                          )}

                          {hasItemFollowUp && (
                            <div className="bg-emerald-900/20 p-3 rounded-xl border border-emerald-500/20">
                              <div className="flex items-center gap-2 mb-2">
                                <FaListCheck className="text-emerald-400 text-xs" />
                                <span className="text-xs text-emerald-400 uppercase font-bold tracking-wide">
                                  {t("poll") || "Опитування"}
                                </span>
                              </div>
                              <p className="text-slate-300 text-sm mb-1 italic">
                                "{moodItem.follow_up.question}"
                              </p>
                              <p className="text-emerald-300 font-semibold text-base pl-2 border-l-2 border-emerald-500">
                                {moodItem.follow_up.option}
                              </p>
                            </div>
                          )}

                          {/* Якщо взагалі нічого немає */}
                          {!hasItemNote && !hasItemFollowUp && !hasItemActivities && (
                            <p className="text-xs text-slate-600 italic pl-1">
                              {t("noDetails") || "Без деталей"}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Calendar;